import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { API_URL, APP_URL } from "./env";

// Client-side auth against the BetterAuth handler the API mounts at /auth/*
// (apps/api/src/server.ts). We deliberately talk to those endpoints with plain
// fetch rather than pulling in the better-auth client SDK: the surface is three
// JSON POSTs + a session read, cookies already flow (credentials:"include",
// matching src/trpc.ts), and adding better-auth to the Expo bundle invites the
// Metro package-exports breakage this repo has had to fight before. If the auth
// surface grows (OAuth, 2FA), revisit the SDK then.
//
// Session is cookie-based and httpOnly, so the client can't read it directly —
// it asks the server who it is via GET /auth/get-session and mirrors the
// answer into React state.

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
}

export type AuthStatus = "loading" | "authed" | "anon";

interface AuthContextValue {
  user: AuthUser | null;
  status: AuthStatus;
  /** Re-read the session from the server (after a flow completes elsewhere).
   * Returns the resolved status so callers can branch on it immediately. */
  refresh: () => Promise<AuthStatus>;
  /** Sign up. Returns true if a session was established immediately
   * (auto-sign-in), false if the account needs email verification first — the
   * API flips this on whether email is actually delivered (see better-auth.ts).
   */
  signUp: (args: {
    email: string;
    password: string;
    name: string;
  }) => Promise<boolean>;
  signIn: (args: { email: string; password: string }) => Promise<void>;
  /** Request a magic-link email (dev: link is logged by the API). */
  sendMagicLink: (args: { email: string }) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// observa tags every event/error with the current user id via a synchronous
// getUserId() callback wired at the provider root (apps/app/app/_layout.tsx).
// That callback runs outside React, so it can't read this context directly —
// AuthProvider mirrors the resolved id into this module-level store and
// getCurrentUserId() reads it at emit time. '0' is observa's sentinel for
// "anonymous / not yet known"; once the user signs in the hub's backfillUserId
// can link the earlier anonymous rows to the real id by sessionId.
let currentUserId = "0";
export function getCurrentUserId(): string {
  return currentUserId;
}

/** POST/GET a /auth/* endpoint, returning parsed JSON or throwing a message-
 * bearing Error on a non-2xx. A GET is implied when `body` is omitted. */
async function authFetch<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_URL}/auth${path}`, {
    method: body === undefined ? "GET" : "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(errorMessage(data, res.status));
  return data as T;
}

/** BetterAuth error bodies are `{ message }` (sometimes nested under `error`);
 * fall back to the status so the user never sees a bare "[object Object]". */
function errorMessage(data: unknown, status: number): string {
  if (data !== null && typeof data === "object") {
    const rec = data as Record<string, unknown>;
    if (typeof rec.message === "string") return rec.message;
    const err = rec.error;
    if (err !== null && typeof err === "object") {
      const inner = (err as Record<string, unknown>).message;
      if (typeof inner === "string") return inner;
    }
  }
  return `Something went wrong (${status}). Please try again.`;
}

/** Narrow the get-session payload to our lightweight user, or null. */
function toUser(data: unknown): AuthUser | null {
  if (data === null || typeof data !== "object") return null;
  const user = (data as Record<string, unknown>).user;
  if (user === null || typeof user !== "object") return null;
  const rec = user as Record<string, unknown>;
  if (typeof rec.id !== "string" || typeof rec.email !== "string") return null;
  return {
    id: rec.id,
    email: rec.email,
    name: typeof rec.name === "string" ? rec.name : null,
  };
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  const refresh = useCallback(async (): Promise<AuthStatus> => {
    try {
      const data = await authFetch<unknown>("/get-session");
      const next = toUser(data);
      setUser(next);
      const status: AuthStatus = next ? "authed" : "anon";
      setStatus(status);
      return status;
    } catch {
      // A failed session read means "treat as logged out" — the calculator
      // works anonymously, so don't strand the user on an error screen.
      setUser(null);
      setStatus("anon");
      return "anon";
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Keep the module-level store observa reads in sync with auth state. Every
  // mutation path (refresh, signUp, signIn, signOut) funnels through setUser,
  // so watching `user` covers them all; sign-out resets to the '0' sentinel.
  useEffect(() => {
    currentUserId = user?.id ?? "0";
  }, [user]);

  const signUp = useCallback(
    async (args: { email: string; password: string; name: string }) => {
      await authFetch("/sign-up/email", args);
      // When verification is required the API doesn't mint a session, so
      // refresh comes back "anon" — report that so the screen can prompt the
      // user to check their inbox instead of routing into a logged-in view.
      return (await refresh()) === "authed";
    },
    [refresh],
  );

  const signIn = useCallback(
    async (args: { email: string; password: string }) => {
      await authFetch("/sign-in/email", args);
      await refresh();
    },
    [refresh],
  );

  const sendMagicLink = useCallback(
    async (args: { email: string }) => {
      // callbackURL is where the browser lands after the link verifies and the
      // session cookie is set. Point it at the app root; the user is then signed
      // in and can reach /my-results.
      await authFetch("/sign-in/magic-link", {
        email: args.email,
        callbackURL: APP_URL,
      });
    },
    [],
  );

  const signOut = useCallback(async () => {
    await authFetch("/sign-out", {});
    setUser(null);
    setStatus("anon");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, refresh, signUp, signIn, sendMagicLink, signOut }),
    [user, status, refresh, signUp, signIn, sendMagicLink, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}
